import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Card, CardContent, Chip, Divider, Sheet, Typography } from '@mui/joy';
import axios from '../util/axiosConfig';
import UserContext from '../context/UserContext';
import { io } from 'socket.io-client';
import { quizSocketDomain } from '../util/domain';
import { canSubmitQuizAnswer, computeQuizTimeLeftSeconds } from '../util/quizTimer';
import {
    LIVE_QUIZ_RECEIVED,
    QUIZ_LEADERBOARD_UPDATED,
    liveQuizEventToPhase,
    parseLiveQuizPayload
} from '../util/liveQuizEvents';
import QuizLeaderboard from './QuizLeaderboard';
import QuizRichText from '../components/QuizRichText';

const normalizeLeaderboardFromApi = (payload) => {
    const data = payload?.data ?? payload;
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.data)) return data.data;
    return [];
};

const StudentQuiz = () => {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const { user, isLoading } = useContext(UserContext);

    const [pageState, setPageState] = useState('loading');
    const [error, setError] = useState(null);
    const [session, setSession] = useState(null);
    const [question, setQuestion] = useState(null);
    const [selectedOption, setSelectedOption] = useState('');
    const [savedOption, setSavedOption] = useState('');
    const [timeLeft, setTimeLeft] = useState(null);
    const [reveal, setReveal] = useState(null);
    const [socketConnected, setSocketConnected] = useState(false);
    const [submittingOption, setSubmittingOption] = useState('');
    const [leaderboardRows, setLeaderboardRows] = useState([]);

    const questionStartMsRef = useRef(null);
    const syncSignatureRef = useRef('');
    const lastQuestionIdRef = useRef(null);
    const lastQuestionIndexRef = useRef(null);
    const activeQuestionSinceRef = useRef({ index: null, startedAt: null });
    const saveAnswerTimerRef = useRef(null);
    const [questionMeta, setQuestionMeta] = useState({ number: 0, total: 0 });

    const redirectToLogin = () => {
        navigate(`/login?redirect=${encodeURIComponent(`/quiz/${sessionId}`)}`);
    };

    const roomPayload = useMemo(() => {
        if (!user) return null;
        return {
            sessionId,
            studentId: user.studentId || user.id,
            name: user.name || null,
            photoPath: null
        };
    }, [user, sessionId]);

    const applyPlayerState = useCallback((data) => {
        const s = data?.session;
        const q = data?.question ?? null;
        if (!s) return;

        const uiPhase = data?.uiPhase;
        const questionId = q?.id ?? null;
        const questionIndex = Number(s.current_question_index ?? 0);
        const questionChanged = questionId != null && lastQuestionIdRef.current !== questionId;
        const indexChanged = lastQuestionIndexRef.current !== null
            && lastQuestionIndexRef.current !== questionIndex;

        if (questionChanged || indexChanged) {
            if (questionId != null) {
                lastQuestionIdRef.current = questionId;
            }
            lastQuestionIndexRef.current = questionIndex;
            setSavedOption('');
            setSelectedOption('');
            setReveal(null);
        }

        const savedAnswer = data?.myAnswer?.selectedOption;
        if (savedAnswer && questionId) {
            setSavedOption(savedAnswer);
            setSelectedOption(savedAnswer);
        }

        const canAnswerNow = Boolean(data?.canSubmitAnswer)
            || (typeof data?.timerSecondsRemaining === 'number' && data.timerSecondsRemaining > 0);

        const signature = [
            s.status,
            s.current_question_index,
            s.current_question_status,
            s.current_question_timer_end,
            questionId,
            uiPhase
        ].join('|');

        if (signature === syncSignatureRef.current) {
            return;
        }
        syncSignatureRef.current = signature;

        setSession(s);
        setQuestion(q);
        setError(null);
        setQuestionMeta({
            number: Number(data?.questionNumber || 0),
            total: Number(data?.totalQuestions || 0)
        });

        const phase = uiPhase || (() => {
            if (s.status === 'completed') return 'completed';
            if (s.status === 'waiting' || s.current_question_status === 'waiting') return 'lobby';
            if (s.current_question_status === 'leaderboard') return 'leaderboard';
            if (s.current_question_status === 'timer_expired') return 'timer_expired';
            if (s.current_question_status === 'timer_active') {
                return canAnswerNow ? 'active_question' : 'timer_expired';
            }
            return 'lobby';
        })();

        if (phase === 'completed') {
            setPageState('completed');
            return;
        }
        if (phase === 'lobby') {
            setPageState('lobby');
            return;
        }
        if (phase === 'leaderboard') {
            setPageState('leaderboard');
            return;
        }

        // Show question whenever faculty has started the timer (don't wait until it expires)
        if (s.current_question_status === 'timer_active') {
            if (questionChanged || indexChanged) {
                activeQuestionSinceRef.current = { index: questionIndex, startedAt: Date.now() };
            }

            let remaining = typeof data?.timerSecondsRemaining === 'number'
                ? data.timerSecondsRemaining
                : computeQuizTimeLeftSeconds(s.current_question_timer_end);
            if ((remaining == null || Number.isNaN(remaining)) && q?.time_limit) {
                const since = activeQuestionSinceRef.current;
                if (since.index === questionIndex && since.startedAt) {
                    const elapsed = Math.floor((Date.now() - since.startedAt) / 1000);
                    remaining = Math.max(0, Number(q.time_limit) - elapsed);
                } else {
                    remaining = Number(q.time_limit);
                    activeQuestionSinceRef.current = { index: questionIndex, startedAt: Date.now() };
                }
            }
            if (typeof remaining === 'number') {
                setTimeLeft(remaining);
            }

            if (questionChanged || indexChanged) {
                questionStartMsRef.current = Date.now();
            }
            setPageState('active_question');
            return;
        }

        if (phase === 'timer_expired' || s.current_question_status === 'timer_expired') {
            if ((questionChanged || indexChanged) && s.current_question_status !== 'timer_expired') {
                setPageState('lobby');
                return;
            }
            setPageState('timer_expired');
            return;
        }

        if (phase === 'active_question') {
            if (questionChanged || indexChanged) {
                questionStartMsRef.current = Date.now();
            }
            setPageState('active_question');
            return;
        }

        setPageState('lobby');
    }, []);

    const fetchPlayerState = useCallback(async () => {
        const res = await axios.get(`/quizzes/session/${sessionId}/player-state`);
        if (!res?.data?.success) {
            throw new Error(res?.data?.errorMessage || 'Failed to fetch session');
        }
        applyPlayerState(res.data.data);
        return res.data.data;
    }, [sessionId, applyPlayerState]);

    const fetchRevealIfNeeded = useCallback(async () => {
        try {
            const res = await axios.post(`/quizzes/session/${sessionId}/reveal`);
            if (res?.data?.success) {
                setReveal(res.data.data);
            }
        } catch {
            // faculty may not have ended timer yet
        }
    }, [sessionId]);

    const bootstrap = async () => {
        setError(null);
        setPageState('loading');
        syncSignatureRef.current = '';
        lastQuestionIdRef.current = null;
        lastQuestionIndexRef.current = null;
        activeQuestionSinceRef.current = { index: null, startedAt: null };

        try {
            const joinRes = await axios.post(`/quizzes/session/${sessionId}/join`);
            if (!joinRes?.data?.success) {
                throw new Error(joinRes?.data?.errorMessage || 'Failed to join');
            }
            await fetchPlayerState();
        } catch (e) {
            const message = e?.response?.data?.errorMessage || e?.message || 'Something went wrong';
            setError(message);
            setPageState('error');
        }
    };

    useEffect(() => {
        if (!sessionId) {
            setError('Missing quiz code');
            setPageState('error');
        }
    }, [sessionId]);

    useEffect(() => {
        if (pageState !== 'active_question') return undefined;
        if (session?.current_question_status !== 'timer_active') return undefined;

        const update = () => {
            const serverLeft = computeQuizTimeLeftSeconds(session?.current_question_timer_end);
            if (serverLeft != null && serverLeft > 0) {
                setTimeLeft(serverLeft);
                return;
            }
            const since = activeQuestionSinceRef.current;
            const limit = Number(question?.time_limit || 0);
            if (since.startedAt && limit > 0) {
                const elapsed = Math.floor((Date.now() - since.startedAt) / 1000);
                setTimeLeft(Math.max(0, limit - elapsed));
            }
        };
        update();
        const id = window.setInterval(update, 250);
        return () => window.clearInterval(id);
    }, [
        pageState,
        session?.current_question_status,
        session?.current_question_timer_end,
        question?.time_limit
    ]);

    useEffect(() => {
        if (pageState !== 'timer_expired') return undefined;
        fetchRevealIfNeeded();
        return undefined;
    }, [pageState, question?.id, fetchRevealIfNeeded]);

    useEffect(() => {
        if (pageState !== 'active_question' || timeLeft == null || timeLeft > 0) return undefined;
        fetchPlayerState();
        return undefined;
    }, [pageState, timeLeft, fetchPlayerState]);

    useEffect(() => {
        if (isLoading || !user || !sessionId) return undefined;
        bootstrap();
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, user, sessionId]);

    useEffect(() => {
        if (isLoading || !user || !sessionId) return undefined;
        return () => {
            axios.post(`/quizzes/session/${sessionId}/leave`).catch(() => {});
        };
    }, [isLoading, user, sessionId]);

    useEffect(() => {
        if (isLoading || !user || !roomPayload) return undefined;

        const socket = io(quizSocketDomain, {
            withCredentials: true,
            transports: ['websocket', 'polling'],
            path: '/socket.io'
        });

        socket.on('connect', () => {
            setSocketConnected(true);
            socket.emit('quiz:join-lobby', roomPayload);
        });

        socket.on('disconnect', () => {
            setSocketConnected(false);
        });

        socket.on('connect_error', () => {
            setSocketConnected(false);
        });

        socket.on('quiz:started', () => {
            setPageState('lobby');
            fetchPlayerState();
        });

        socket.on('quiz:question-active', (payload) => {
            syncSignatureRef.current = '';
            lastQuestionIdRef.current = null;
            setReveal(null);
            setSavedOption('');
            setSelectedOption('');
            fetchPlayerState();
        });

        socket.on('quiz:question-ended', (payload) => {
            syncSignatureRef.current = '';
            if (payload?.correctOption || payload?.explanation || payload?.stats) {
                setReveal({
                    correctOption: payload.correctOption,
                    explanation: payload.explanation,
                    stats: payload.stats
                });
            } else {
                setReveal(null);
                fetchRevealIfNeeded();
            }
            setPageState('timer_expired');
            fetchPlayerState();
        });

        socket.on('quiz:leaderboard-update', (payload) => {
            const list = Array.isArray(payload?.leaderboard) ? payload.leaderboard : [];
            if (list.length > 0) {
                setLeaderboardRows(list);
                window.dispatchEvent(new CustomEvent(QUIZ_LEADERBOARD_UPDATED, {
                    detail: { sessionId, leaderboard: list }
                }));
            } else {
                axios.get(`/quizzes/session/${sessionId}/results`)
                    .then((res) => {
                        if (!res?.data?.success) return;
                        const rows = normalizeLeaderboardFromApi(res.data);
                        setLeaderboardRows(rows);
                        window.dispatchEvent(new CustomEvent(QUIZ_LEADERBOARD_UPDATED, {
                            detail: { sessionId, leaderboard: rows }
                        }));
                    })
                    .catch(() => {});
            }
            setPageState('leaderboard');
            fetchPlayerState();
        });

        socket.on('quiz:completed', (payload) => {
            const podium = Array.isArray(payload?.podium) ? payload.podium : [];
            if (podium.length > 0) {
                setLeaderboardRows(podium);
                window.dispatchEvent(new CustomEvent(QUIZ_LEADERBOARD_UPDATED, {
                    detail: { sessionId, leaderboard: podium }
                }));
            } else {
                axios.get(`/quizzes/session/${sessionId}/results`)
                    .then((res) => {
                        if (!res?.data?.success) return;
                        const rows = normalizeLeaderboardFromApi(res.data);
                        setLeaderboardRows(rows);
                        window.dispatchEvent(new CustomEvent(QUIZ_LEADERBOARD_UPDATED, {
                            detail: { sessionId, leaderboard: rows }
                        }));
                    })
                    .catch(() => {});
            }
            setPageState('completed');
            fetchPlayerState();
        });

        socket.on('quiz:answer-submitted', () => {
            fetchPlayerState();
        });

        socket.on('quiz:player-state-updated', () => {
            fetchPlayerState();
        });

        return () => {
            socket.removeAllListeners();
            socket.disconnect();
            setSocketConnected(false);
        };
    }, [isLoading, user, roomPayload, fetchPlayerState, fetchRevealIfNeeded]);

    useEffect(() => {
        if (!sessionId) return undefined;

        const onLiveQuizPush = (event) => {
            const detail = event?.detail;
            const parsed = detail?.event ? detail : parseLiveQuizPayload(detail?.raw || detail);
            if (!parsed || parsed.sessionId !== sessionId) return;

            const phase = liveQuizEventToPhase(parsed.event);
            if (phase === 'timer_expired') {
                fetchRevealIfNeeded();
            }
            if (phase === 'leaderboard' || phase === 'completed') {
                axios.get(`/quizzes/session/${sessionId}/results`)
                    .then((res) => {
                        if (res?.data?.success) {
                            const list = normalizeLeaderboardFromApi(res.data);
                            setLeaderboardRows(list);
                        }
                    })
                    .catch(() => {});
            }
            if (phase) {
                setPageState(phase);
            }
            fetchPlayerState();
        };

        const onLeaderboardSocket = (event) => {
            if (event?.detail?.sessionId === sessionId && Array.isArray(event.detail.leaderboard)) {
                setLeaderboardRows(event.detail.leaderboard);
            }
        };

        window.addEventListener(LIVE_QUIZ_RECEIVED, onLiveQuizPush);
        window.addEventListener(QUIZ_LEADERBOARD_UPDATED, onLeaderboardSocket);
        return () => {
            window.removeEventListener(LIVE_QUIZ_RECEIVED, onLiveQuizPush);
            window.removeEventListener(QUIZ_LEADERBOARD_UPDATED, onLeaderboardSocket);
        };
    }, [sessionId, fetchPlayerState, fetchRevealIfNeeded]);

    const saveAnswer = useCallback(async (option) => {
        if (!question?.id || !option) return;
        if (!canSubmitQuizAnswer(session)) {
            setError('Time is up for this question.');
            return;
        }

        setSubmittingOption(option);
        setError(null);

        try {
            const startMs = questionStartMsRef.current || Date.now();
            const responseMs = Math.max(0, Date.now() - startMs);
            const res = await axios.post(`/quizzes/session/${sessionId}/answer`, {
                questionId: question.id,
                selectedOption: option,
                clientResponseTimeMs: responseMs
            });
            if (!res?.data?.success) {
                throw new Error(res?.data?.errorMessage || 'Failed to save');
            }
            const saved = res.data.data?.selectedOption || option;
            setSavedOption(saved);
            setSelectedOption(saved);
        } catch (e) {
            const message = e?.response?.data?.errorMessage || e?.message || 'Failed to save answer';
            if (message.includes('Time is up') || message.includes('no longer accepted')) {
                setPageState('timer_expired');
                fetchRevealIfNeeded();
                return;
            }
            setError(message);
        } finally {
            setSubmittingOption('');
        }
    }, [question?.id, session, sessionId, fetchRevealIfNeeded]);

    const scheduleSaveAnswer = useCallback((option) => {
        if (saveAnswerTimerRef.current) {
            window.clearTimeout(saveAnswerTimerRef.current);
        }
        saveAnswerTimerRef.current = window.setTimeout(() => {
            saveAnswer(option);
        }, 350);
    }, [saveAnswer]);

    const handleOptionChange = (option) => {
        if (pageState !== 'active_question' || !question?.id) return;
        setSelectedOption(option);
        scheduleSaveAnswer(option);
    };

    useEffect(() => () => {
        if (saveAnswerTimerRef.current) {
            window.clearTimeout(saveAnswerTimerRef.current);
        }
    }, []);

    if (!sessionId) return null;

    if (isLoading) {
        return (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                <Typography>Loading...</Typography>
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                <Card variant="outlined" sx={{ maxWidth: 520, width: '100%' }}>
                    <CardContent>
                        <Typography level="h3" sx={{ mb: 1 }}>Live Quiz</Typography>
                        <Typography level="body-sm" sx={{ mb: 2 }}>
                            Log in to participate in quiz <strong>{sessionId}</strong>.
                        </Typography>
                        <Button
                            onClick={redirectToLogin}
                            sx={{ bgcolor: '#ea7a23', '&:hover': { bgcolor: '#d06a1e' } }}
                        >
                            Log In to Participate
                        </Button>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    const headerTitle = session?.quiz_title ? `${session.quiz_title}` : 'Live Quiz';
    const showQuestion = question?.question_text && (
        pageState === 'active_question' || pageState === 'timer_expired'
    );
    const displayChoice = savedOption || selectedOption;
    const revealedCorrect = reveal?.correctOption
        ? String(reveal.correctOption).trim().toUpperCase()
        : '';
    const isYourAnswerCorrect = Boolean(
        displayChoice
        && revealedCorrect
        && String(displayChoice).trim().toUpperCase() === revealedCorrect
    );

    return (
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', minWidth: 0 }}>
            <Card variant="outlined" sx={{ maxWidth: 860, width: '100%', minWidth: 0 }}>
                <CardContent sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography level="h3" sx={{ mb: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {headerTitle}
                            </Typography>
                            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                Code: <strong>{sessionId}</strong>
                                {questionMeta.number > 0 && questionMeta.total > 0 && (
                                    <> · Question <strong>{questionMeta.number}</strong> of {questionMeta.total}</>
                                )}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end' }}>
                            <Chip size="sm" variant="soft" color={socketConnected ? 'success' : 'neutral'}>
                                {socketConnected ? 'Live connected' : 'Connecting…'}
                            </Chip>
                            <Chip color="warning" variant="soft">
                                {pageState.replace(/_/g, ' ')}
                            </Chip>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    {pageState === 'loading' && (
                        <Typography>Loading quiz…</Typography>
                    )}

                    {pageState === 'error' && (
                        <Sheet variant="soft" color="danger" sx={{ p: 1.5, borderRadius: 'sm' }}>
                            <Typography level="title-sm">Unable to continue</Typography>
                            <Typography level="body-sm" sx={{ mt: 0.5 }}>
                                {error || 'Something went wrong.'}
                            </Typography>
                            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button variant="solid" color="warning" onClick={bootstrap}>
                                    Retry
                                </Button>
                                <Button variant="plain" onClick={() => navigate('/')}>
                                    Back to home
                                </Button>
                            </Box>
                        </Sheet>
                    )}

                    {pageState === 'lobby' && (
                        <Box>
                            <Typography level="title-md" sx={{ mb: 0.5 }}>
                                {question?.question_text ? 'Get ready…' : 'Waiting for faculty to start…'}
                            </Typography>
                            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                {question?.question_text
                                    ? 'The next question will appear here when the timer starts.'
                                    : 'Stay on this screen. Questions appear automatically when the faculty starts the quiz.'}
                            </Typography>
                        </Box>
                    )}

                    {pageState === 'active_question' && (
                        <Box sx={{ minWidth: 0 }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
                                <Chip color={timeLeft !== null && timeLeft <= 5 ? 'danger' : 'warning'} variant="solid">
                                    Time left: {timeLeft ?? '—'}s
                                </Chip>
                                {displayChoice && (
                                    <Chip color="primary" variant="soft">
                                        Your choice: {displayChoice}
                                        {submittingOption === displayChoice ? ' (saving…)' : savedOption === displayChoice ? ' (saved)' : ''}
                                    </Chip>
                                )}
                            </Box>

                            {question?.question_text ? (
                                <QuizRichText
                                    content={question.question_text}
                                    sx={{ mb: 1, fontSize: '1.1rem', fontWeight: 600 }}
                                />
                            ) : (
                                <Typography level="title-md" sx={{ mb: 1 }}>
                                    Question will appear shortly…
                                </Typography>
                            )}

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {(['A', 'B', 'C', 'D']).map((opt) => {
                                    const label = question ? question[`option_${opt.toLowerCase()}`] : '';
                                    const isSelected = selectedOption === opt;
                                    return (
                                        <Sheet
                                            key={opt}
                                            variant={isSelected ? 'soft' : 'outlined'}
                                            color={isSelected ? 'warning' : 'neutral'}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => handleOptionChange(opt)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    handleOptionChange(opt);
                                                }
                                            }}
                                            sx={{
                                                p: 1.25,
                                                borderRadius: '5px',
                                                cursor: 'pointer',
                                                border: '2px solid',
                                                borderColor: isSelected ? '#ea7a23' : 'divider',
                                                bgcolor: isSelected ? 'rgba(234, 122, 35, 0.08)' : 'background.surface',
                                                '&:hover': {
                                                    borderColor: '#ea7a23',
                                                    bgcolor: 'rgba(234, 122, 35, 0.06)'
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                                <Chip size="sm" variant="solid" color={isSelected ? 'warning' : 'neutral'}>
                                                    {opt}
                                                </Chip>
                                                {label ? (
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <QuizRichText
                                                            content={label}
                                                            disablePointerEvents
                                                            sx={{ fontSize: '0.95rem' }}
                                                        />
                                                    </Box>
                                                ) : (
                                                    <Typography level="body-sm" sx={{ flex: 1, color: 'text.tertiary' }}>
                                                        —
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Sheet>
                                    );
                                })}
                            </Box>

                            {error && (
                                <Typography level="body-sm" sx={{ mt: 1, color: 'danger.500' }}>
                                    {error}
                                </Typography>
                            )}
                            <Typography level="body-xs" sx={{ mt: 1, color: 'text.secondary' }}>
                                Select an option — you can change your answer until the timer ends. The correct answer is shown after time is up.
                            </Typography>
                        </Box>
                    )}

                    {pageState === 'timer_expired' && (
                        <Box sx={{ minWidth: 0 }}>
                            {showQuestion && (
                                <>
                                    <QuizRichText
                                        content={question.question_text}
                                        sx={{ mb: 1, fontSize: '1.1rem', fontWeight: 600 }}
                                    />
                                    {displayChoice && (
                                        <Chip color="primary" variant="soft" sx={{ mb: 1 }}>
                                            Your answer: {displayChoice}
                                        </Chip>
                                    )}
                                </>
                            )}
                            <Typography level="title-md" sx={{ mb: 1 }}>
                                Time is up
                            </Typography>
                            {isYourAnswerCorrect && (
                                <Sheet
                                    variant="soft"
                                    color="success"
                                    sx={{
                                        p: 1.5,
                                        mb: 1.5,
                                        borderRadius: 'sm',
                                        border: '1px solid',
                                        borderColor: 'success.300'
                                    }}
                                >
                                    <Typography level="title-md" sx={{ color: 'success.700' }}>
                                        Congratulations — correct answer!
                                    </Typography>
                                    <Typography level="body-sm" sx={{ mt: 0.5, color: 'success.800' }}>
                                        You chose option {displayChoice}. Well done.
                                    </Typography>
                                </Sheet>
                            )}
                            {displayChoice && revealedCorrect && !isYourAnswerCorrect && (
                                <Sheet variant="soft" color="neutral" sx={{ p: 1.25, mb: 1.5, borderRadius: 'sm' }}>
                                    <Typography level="body-sm">
                                        Your answer was {displayChoice}. The correct option was {revealedCorrect}.
                                    </Typography>
                                </Sheet>
                            )}
                            {reveal?.correctOption ? (
                                <Chip color="success" variant="solid" sx={{ mb: 1 }}>
                                    Correct option: {reveal.correctOption}
                                </Chip>
                            ) : (
                                <Chip color="warning" variant="soft" sx={{ mb: 1 }}>
                                    Waiting for reveal…
                                </Chip>
                            )}
                            {reveal?.explanation && (
                                <Sheet variant="soft" sx={{ p: 1.25, borderRadius: 'sm' }}>
                                    <Typography level="title-sm">Explanation</Typography>
                                    <Box sx={{ mt: 0.5 }}>
                                        <QuizRichText content={reveal.explanation} />
                                    </Box>
                                </Sheet>
                            )}
                            <Typography level="body-sm" sx={{ mt: 1.5, color: 'text.secondary' }}>
                                Please wait, your screen will update automatically.
                            </Typography>
                        </Box>
                    )}

                    {pageState === 'leaderboard' && (
                        <QuizLeaderboard
                            sessionId={sessionId}
                            currentStudentId={user?.studentId || user?.id}
                            externalRows={leaderboardRows.length > 0 ? leaderboardRows : null}
                            disablePolling={socketConnected}
                        />
                    )}

                    {pageState === 'completed' && (
                        <Box sx={{ minWidth: 0 }}>
                            <Typography level="title-md" sx={{ mb: 0.5 }}>
                                Quiz completed
                            </Typography>
                            <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 1 }}>
                                Thanks for participating. Here are the final standings.
                            </Typography>
                            <QuizLeaderboard
                                sessionId={sessionId}
                                currentStudentId={user?.studentId || user?.id}
                                variant="final"
                                externalRows={leaderboardRows.length > 0 ? leaderboardRows : null}
                                disablePolling={socketConnected}
                            />
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default StudentQuiz;
